// scaleCheck.js — work out a sheet's scale and check it against the sheet's own dimension strings.
//
//   checkSheetScale(sheet, printed) → {
//     ok, ftPerUnit,                     // feet per sheet unit (PDF point at scale 1) — set when ok
//     label, source,                     // "printed" | "dimensions" | "printed+dims"
//     dims: { found, measured, agree },  // dimension strings read / measured / agreeing with the scale used
//     warn, reason,                      // warn: scale set but worth a look; reason: why (or why not)
//   }
//   printed = detectScaleFromText(sheet.text) result (or null)
//
// How dimensions are measured: a dimension string (12'-6") sits on its dimension line; the line ends at
// ticks / arrows / extension lines that cross it. Length between the nearest crossings either side of the
// text ↔ the written value gives a scale. Scales most dimensions agree on win. That catches sheets with
// several printed scales (plan + details), sheets with none, and sets printed reduced (11×17 of a 24×36).

const PT_PER_IN = 72;
const SEG_CURVE = 4;

// standard scales as feet per paper inch
const STANDARD = [
  ...[
    [1 / 32, '1/32"'],
    [1 / 16, '1/16"'],
    [3 / 32, '3/32"'],
    [1 / 8, '1/8"'],
    [3 / 16, '3/16"'],
    [1 / 4, '1/4"'],
    [3 / 8, '3/8"'],
    [1 / 2, '1/2"'],
    [3 / 4, '3/4"'],
    [1, '1"'],
    [1.5, '1 1/2"'],
    [3, '3"'],
  ].map(([inch, l]) => ({ ftPerIn: 1 / inch, label: `${l} = 1'-0"` })),
  ...[10, 20, 30, 40, 50, 60, 100].map((f) => ({
    ftPerIn: f,
    label: `1" = ${f}'`,
  })),
  ...[20, 25, 50, 75, 100, 200, 250, 500].map((r) => ({
    ftPerIn: r / 12,
    label: `1:${r}`,
  })),
];
const rel = (a, b) => Math.abs(a - b) / b;

// 12'-6"  12' - 6 1/2"  12'  12'-0  → feet; inches-only and tiny values are skipped (too noisy)
export function parseDimFeet(str) {
  const s = String(str)
    .replace(/[’′]/g, "'")
    .replace(/[”″]/g, '"')
    .replace(/\s+/g, " ")
    .trim();
  const m = s.match(
    /^(\d{1,3})\s*'\s*(?:-?\s*(\d{1,2})(?:\s+(\d{1,2})\/(\d{1,2}))?\s*"?|-?\s*(\d{1,2})\/(\d{1,2})\s*"?)?$/,
  );
  if (!m) return null;
  let ft = +m[1];
  if (m[2] != null) ft += +m[2] / 12 + (m[3] ? +m[3] / +m[4] / 12 : 0);
  else if (m[5] != null) ft += +m[5] / +m[6] / 12;
  if (m[2] != null && +m[2] >= 12) return null;
  return ft >= 1.5 ? ft : null;
}

function gridIndex(segs, n, cell) {
  const g = new Map();
  for (let i = 0; i < n; i++) {
    const ax = segs[i * 4],
      ay = segs[i * 4 + 1],
      bx = segs[i * 4 + 2],
      by = segs[i * 4 + 3];
    const cx0 = Math.floor(Math.min(ax, bx) / cell),
      cx1 = Math.floor(Math.max(ax, bx) / cell);
    const cy0 = Math.floor(Math.min(ay, by) / cell),
      cy1 = Math.floor(Math.max(ay, by) / cell);
    if ((cx1 - cx0 + 1) * (cy1 - cy0 + 1) > 400) continue; // page borders etc.
    for (let cx = cx0; cx <= cx1; cx++)
      for (let cy = cy0; cy <= cy1; cy++) {
        const k = cx * 100003 + cy;
        let a = g.get(k);
        if (!a) g.set(k, (a = []));
        a.push(i);
      }
  }
  return (x0, y0, x1, y1) => {
    const out = new Set();
    for (let cx = Math.floor(x0 / cell); cx <= Math.floor(x1 / cell); cx++)
      for (let cy = Math.floor(y0 / cell); cy <= Math.floor(y1 / cell); cy++) {
        const a = g.get(cx * 100003 + cy);
        if (a) for (const i of a) out.add(i);
      }
    return out;
  };
}

// every dimension string's measured length (sheet units) — [{ ft, lens:[...] }]
// metric sets dimension in millimetres: 3600, 1 200, 12 450
export function parseDimMm(str) {
  const m = String(str)
    .trim()
    .match(/^(\d{1,2}[ ,]?\d{3}|\d{3,5})$/);
  if (!m) return null;
  const mm = +m[1].replace(/[ ,]/g, "");
  return mm >= 450 && mm <= 60000 ? mm / 304.8 : null;
}

// inch-only strings (5", 3 1/2") — too small to check a scale with, but they still mark dimension lines
export function parseDimInches(str) {
  const m = String(str)
    .replace(/[”″]/g, '"')
    .trim()
    .match(/^(\d{1,2})(?:\s*(\d{1,2})\/(\d{1,2}))?\s*"$/);
  return m ? (+m[1] + (m[2] ? +m[2] / +m[3] : 0)) / 12 : null;
}

export function measureDims(sheet, metric = false) {
  const parse =
    metric === "inches" ? parseDimInches : metric ? parseDimMm : parseDimFeet;
  const S = sheet.segs,
    F = sheet.segF,
    n = S.length >> 2;
  const near = gridIndex(S, n, 40);
  const out = [];
  for (const t of sheet.text || []) {
    const ft = parse(t.str);
    if (ft == null) continue;
    const r = ((t.rot % 360) + 360) % 360;
    // some PDFs report vertical dimension text with rotation 0 — a box much taller than wide gives it away
    const bw = t.x1 - t.x0,
      bh = t.y1 - t.y0;
    const tall = String(t.str).length >= 2 && bh > bw * 1.6;
    const horiz = (r === 0 || r === 180) && !tall,
      vert = r === 90 || r === 270 || ((r === 0 || r === 180) && tall);
    if (!horiz && !vert) continue;
    // u: along the dimension line, v: across it
    const cu = horiz ? (t.x0 + t.x1) / 2 : (t.y0 + t.y1) / 2;
    const cv = horiz ? (t.y0 + t.y1) / 2 : (t.x0 + t.x1) / 2;
    const h = Math.max(
      1,
      Math.min(t.h || 6, horiz ? t.y1 - t.y0 : t.x1 - t.x0) || 6,
    );
    const half = (horiz ? t.x1 - t.x0 : t.y1 - t.y0) / 2;
    const reachV = h * 2.2,
      reachU = Math.max(half * 40, 600);
    const ids = horiz
      ? near(cu - reachU, cv - reachV, cu + reachU, cv + reachV)
      : near(cv - reachV, cu - reachU, cv + reachV, cu + reachU);
    const seg = (i) => {
      const ax = S[i * 4],
        ay = S[i * 4 + 1],
        bx = S[i * 4 + 2],
        by = S[i * 4 + 3];
      return horiz ? [ax, ay, bx, by] : [ay, ax, by, bx]; // (u, v) coords
    };
    // dimension line: parallel, just beside / through the text, reaching past the text's middle
    const lines = [];
    for (const i of ids) {
      if (F[i] & SEG_CURVE) continue;
      const [au, av, bu, bv] = seg(i);
      if (Math.abs(av - bv) > 0.5) continue;
      const lv = (av + bv) / 2,
        dv = Math.abs(lv - cv);
      if (dv > h * 1.8) continue;
      const u0 = Math.min(au, bu),
        u1 = Math.max(au, bu);
      if (u1 - u0 < half * 0.5) continue;
      // through the middle, or broken around the text (a piece ending at the text either side)
      if (
        (u0 <= cu && u1 >= cu) ||
        Math.abs(u1 - (cu - half)) < h * 1.5 ||
        Math.abs(u0 - (cu + half)) < h * 1.5
      )
        lines.push({ lv, u0, u1 });
    }
    if (!lines.length) continue;
    // pick the line row nearest the text (pieces at the same offset belong together)
    lines.sort((a, b) => Math.abs(a.lv - cv) - Math.abs(b.lv - cv));
    const lv = lines[0].lv;
    const row = lines.filter((l) => Math.abs(l.lv - lv) < 0.6);
    let lu0 = Math.min(...row.map((l) => l.u0)),
      lu1 = Math.max(...row.map((l) => l.u1));
    // terminators: anything not parallel that crosses (or touches) the line row
    const tol = Math.max(0.6, h * 0.35);
    const ts = [];
    const ids2 = horiz
      ? near(lu0 - 2, lv - h * 2, lu1 + 2, lv + h * 2)
      : near(lv - h * 2, lu0 - 2, lv + h * 2, lu1 + 2);
    for (const i of ids2) {
      const [au, av, bu, bv] = seg(i);
      if (Math.abs(av - bv) <= 0.5) continue; // parallel
      const vmin = Math.min(av, bv),
        vmax = Math.max(av, bv);
      if (vmax < lv - tol || vmin > lv + tol) continue; // doesn't reach the line
      const k = Math.abs(bv - av) < 1e-9 ? 0.5 : (lv - av) / (bv - av);
      const u = au + (bu - au) * Math.max(0, Math.min(1, k));
      if (u < lu0 - tol || u > lu1 + tol) continue;
      ts.push(u);
    }
    const lens = [];
    const left = ts.filter((u) => u < cu - half * 0.6).sort((a, b) => b - a)[0];
    const right = ts
      .filter((u) => u > cu + half * 0.6)
      .sort((a, b) => a - b)[0];
    if (left != null && right != null) lens.push(right - left);
    // the line itself, when it stops at its extension lines
    if (row.length <= 2 && lu1 - lu0 > half * 1.2) lens.push(lu1 - lu0);
    // geometry too (sheet units): the dimension line, so tracing can stay off dimension strings
    const line = horiz
      ? { a: [lu0, lv], b: [lu1, lv], h }
      : { a: [lv, lu0], b: [lv, lu1], h };
    if (lens.length) out.push({ ft, lens, line });
  }
  return out;
}

// how many dimensions agree with ftPerUnit (within tol)
function support(dims, f, tol = 0.025) {
  let k = 0;
  for (const d of dims) if (d.lens.some((L) => rel(d.ft / L, f) <= tol)) k++;
  return k;
}

export function snapStandard(ftPerUnit, tol = 0.012) {
  const ftPerIn = ftPerUnit * PT_PER_IN;
  let best = null;
  for (const s of STANDARD) {
    const e = rel(ftPerIn, s.ftPerIn);
    if (e <= tol && (!best || e < best.e)) best = { ...s, e };
  }
  return best
    ? { label: best.label, ftPerUnit: best.ftPerIn / PT_PER_IN }
    : null;
}

const fmtRatio = (f) => {
  const s = snapStandard(f, 0.012);
  if (s) return s.label;
  const inPerFt = 1 / (f * PT_PER_IN); // paper inches per foot
  return `${inPerFt.toFixed(3)}" = 1'-0"`;
};

export function checkSheetScale(sheet, printed) {
  // imperial dimensions, else millimetres (metric scale printed, or no feet-inch strings at all)
  let dims = measureDims(sheet),
    parse = parseDimFeet;
  const metricPrinted = (printed?.all || []).some((p) => /^1:/.test(p.label));
  if (
    dims.length < 3 &&
    (metricPrinted ||
      !(sheet.text || []).some((t) => parseDimFeet(t.str) != null))
  ) {
    const mm = measureDims(sheet, true);
    if (mm.length > dims.length) {
      dims = mm;
      parse = parseDimMm;
    }
  }
  const found = (sheet.text || []).filter((t) => parse(t.str) != null).length;
  const info = (agree) => ({ found, measured: dims.length, agree });

  // the scale the dimensions agree on
  let cons = null;
  if (dims.length >= 3) {
    const cands = [];
    for (const d of dims)
      for (const L of d.lens) if (L > 2) cands.push(d.ft / L);
    for (const c of cands) {
      const k = support(dims, c);
      if (!cons || k > cons.k) cons = { f: c, k };
    }
    if (cons) {
      // refine: median of the agreeing ratios
      const vals = [];
      for (const d of dims) {
        const r = d.lens
          .map((L) => d.ft / L)
          .filter((x) => rel(x, cons.f) <= 0.025);
        if (r.length) vals.push(r[0]);
      }
      vals.sort((a, b) => a - b);
      cons.f = vals[vals.length >> 1];
      cons.k = support(dims, cons.f);
    }
  }
  const consOk = cons && cons.k >= 4 && cons.k >= dims.length * 0.3;

  // printed scales, checked against the dimensions
  const opts = (printed?.all || []).map((p) => ({
    ...p,
    k: support(dims, p.ftPerUnit),
  }));
  opts.sort((a, b) => b.k - a.k || b.count - a.count);
  const best = opts[0];

  const options = opts.map((p) => ({
    label: p.label,
    ftPerUnit: p.ftPerUnit,
    agree: p.k,
  }));
  const plural = (k) => `${k} dimension${k === 1 ? "" : "s"}`;

  // 1. a printed scale the dimensions back up
  if (best && best.k >= 3 && (!consOk || best.k >= cons.k * 0.7)) {
    return {
      ok: true,
      ftPerUnit: best.ftPerUnit,
      label: best.label,
      source: "printed+dims",
      dims: info(best.k),
      warn: false,
      options,
      reason: `${best.label} printed · ${plural(best.k)} agree`,
    };
  }
  if (consOk) {
    // 2. dimensions land near a printed scale (detail sheets drawn loosely, dims to the nearest 5 mm): the printed one
    const nearP = opts
      .filter((p) => rel(cons.f, p.ftPerUnit) <= 0.06)
      .sort((a, b) => rel(cons.f, a.ftPerUnit) - rel(cons.f, b.ftPerUnit))[0];
    if (nearP) {
      return {
        ok: true,
        ftPerUnit: nearP.ftPerUnit,
        label: nearP.label,
        source: "printed+dims",
        dims: info(support(dims, nearP.ftPerUnit, 0.06)),
        warn: false,
        options,
        reason: `${nearP.label} printed · ${plural(support(dims, nearP.ftPerUnit, 0.06))} agree`,
      };
    }
    // 2b. loose dimensions that don't land on any standard scale but sit within 10% of the printed one: printed, flagged
    const snap0 = snapStandard(cons.f);
    const nearP2 =
      !snap0 &&
      opts
        .filter((p) => rel(cons.f, p.ftPerUnit) <= 0.1)
        .sort((a, b) => rel(cons.f, a.ftPerUnit) - rel(cons.f, b.ftPerUnit))[0];
    if (nearP2) {
      return {
        ok: true,
        ftPerUnit: nearP2.ftPerUnit,
        label: nearP2.label,
        source: "printed",
        dims: info(support(dims, nearP2.ftPerUnit, 0.1)),
        warn: true,
        options,
        reason: `${nearP2.label} printed — the dimensions only roughly agree (${fmtRatio(cons.f)}), check one`,
      };
    }
    // 3. the dimensions alone (no printed scale, or one that's clearly off — a set printed reduced).
    //    Overruling a printed scale takes strong evidence: 8+ dimensions and half of those measured.
  }
  if (consOk && (!best || (cons.k >= 8 && cons.k >= dims.length * 0.5))) {
    const snap = snapStandard(cons.f);
    const f = snap ? snap.ftPerUnit : cons.f;
    const k = support(dims, f);
    return {
      ok: true,
      ftPerUnit: f,
      label: snap ? snap.label : fmtRatio(f),
      source: "dimensions",
      dims: info(k),
      warn: !snap || !!best,
      options,
      reason: best
        ? `dimensions say ${fmtRatio(f)} (${k} agree), not the printed ${best.label} — check one${snap ? "" : " (set printed at reduced size?)"}`
        : snap
          ? `read from ${plural(k)}`
          : `read from ${plural(k)} — not a standard scale (reduced print?)`,
    };
  }
  // 4. one printed scale the dimensions neither confirm nor contradict: take it, flagged for a look if dims existed
  if (printed && !printed.multi) {
    const checked = dims.length >= 4;
    return {
      ok: true,
      ftPerUnit: printed.ftPerUnit,
      label: printed.label,
      source: "printed",
      dims: info(best?.k || 0),
      warn: checked,
      options,
      reason: checked
        ? `${printed.label} printed — the dimensions couldn't confirm it, check one`
        : `${printed.label} printed (no dimensions to check)`,
    };
  }
  if (printed?.multi)
    return {
      ok: false,
      dims: info(0),
      options,
      reason: `several scales printed (${printed.all.map((a) => a.label).join(", ")}) — pick the plan's`,
    };
  return {
    ok: false,
    dims: info(0),
    options,
    reason: found
      ? "no printed scale and too few readable dimensions"
      : "no scale or dimensions found — not a plan sheet?",
  };
}

// every dimension line on the sheet ([{ a, b, h }] sheet units) — feet-inch strings, else millimetres
export function dimensionLines(sheet) {
  let dims = measureDims(sheet);
  if (dims.length < 3) {
    const mm = measureDims(sheet, true);
    if (mm.length > dims.length) dims = mm;
  }
  return [...dims, ...measureDims(sheet, "inches")].map((d) => d.line);
}
